import React, { useState, useRef } from 'react';
import Navbar from '../components/Navbar';
import { trackToolAction } from '../utils/metrics';
import { ReviewCaveat, GeneratedLegend } from '../components/ReviewAid';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';

// ─── CSV Utilities ─────────────────────────────────────────────────────────────

function splitCSVLine(line) {
  const result = [];
  let current = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    if (char === '"') {
      if (inQuotes && line[i + 1] === '"') { current += '"'; i++; }
      else inQuotes = !inQuotes;
    } else if (char === ',' && !inQuotes) {
      result.push(current); current = '';
    } else {
      current += char;
    }
  }
  result.push(current);
  return result;
}

function parseCSV(text) {
  text = text.replace(/^\uFEFF/, '');
  const lines = text.split(/\r?\n/).filter(l => l.trim());
  if (lines.length < 2) return [];
  const headers = splitCSVLine(lines[0]);
  return lines.slice(1).map(line => {
    const values = splitCSVLine(line);
    const row = {};
    headers.forEach((h, i) => { row[h.trim()] = (values[i] || '').trim(); });
    return row;
  });
}

function readFileText(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = e => resolve(e.target.result);
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsText(file, 'UTF-8');
  });
}

function parseRegion(region) {
  if (!region) return '';
  const parts = region.toUpperCase().split('-');
  return parts.length >= 2 ? parts[parts.length - 1] : parts[0];
}

function fmtConf(val) {
  const n = parseFloat(val);
  return isNaN(n) ? 0 : n;
}

function bestVehicleField(value, confidence, threshold = 50) {
  if (fmtConf(confidence) >= threshold && value) {
    return value.trim().replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  }
  return '';
}

function fmtSize(b) {
  if (b < 1024) return b + ' B';
  if (b < 1048576) return (b / 1024).toFixed(1) + ' KB';
  return (b / 1048576).toFixed(1) + ' MB';
}

function fmtTimestamp(ts) {
  if (!ts) return '';
  try {
    const d = new Date(ts);
    if (isNaN(d)) return ts;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' })
      + '  ' + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  } catch { return ts; }
}

async function analyzeFiles(files, minConfidence) {
  const plateSites = {};
  const plateVehicle = {};
  const plateState = {};
  const allSites = new Set();
  let filesProcessed = 0;

  for (const file of files) {
    let text;
    try { text = await readFileText(file); } catch { continue; }
    const rows = parseCSV(text);
    filesProcessed++;

    for (const row of rows) {
      const plate = (row['best_plate'] || '').trim().toUpperCase();
      if (!plate) continue;
      const confidence = fmtConf(row['best_confidence'] || '0');
      if (confidence < minConfidence) continue;

      const site = (row['site'] || '').trim() || (row['camera'] || '').trim();
      const camera = (row['camera'] || '').trim();
      const ts = (row['epoch_time_start'] || '').trim();
      const region = (row['region'] || '').trim();
      if (!site) continue;

      allSites.add(site);
      if (!plateSites[plate]) plateSites[plate] = {};
      if (!plateSites[plate][site]) {
        plateSites[plate][site] = { camera: '', reads: 0, first_seen: '', last_seen: '' };
      }
      const entry = plateSites[plate][site];
      entry.reads++;
      if (camera) entry.camera = camera;
      if (ts) {
        if (!entry.first_seen || ts < entry.first_seen) entry.first_seen = ts;
        if (!entry.last_seen || ts > entry.last_seen) entry.last_seen = ts;
      }

      const color = bestVehicleField(row['vehicle_color'] || '', row['vehicle_color_confidence'] || '0', 40);
      const body = bestVehicleField(row['vehicle_body_type'] || '', row['vehicle_body_type_confidence'] || '0', 40);
      const make = bestVehicleField(row['vehicle_make'] || '', row['vehicle_make_confidence'] || '0', 60);
      const parts = [color, make, body].filter(Boolean);
      if (parts.length) plateVehicle[plate] = parts.join(' ');

      if (region && !plateState[plate]) plateState[plate] = parseRegion(region);
    }
  }

  if (filesProcessed === 0) throw new Error('No valid CSV files found in the upload.');

  const results = [];
  for (const [plate, sitesDict] of Object.entries(plateSites)) {
    const sitesList = Object.entries(sitesDict).map(([name, data]) => ({
      name, camera: data.camera, reads: data.reads,
      first_seen: data.first_seen, last_seen: data.last_seen,
    })).sort((a, b) => b.reads - a.reads);

    results.push({
      plate, state: plateState[plate] || '',
      site_count: sitesList.length,
      read_count: sitesList.reduce((s, r) => s + r.reads, 0),
      vehicle: plateVehicle[plate] || '',
      sites: sitesList,
    });
  }

  results.sort((a, b) =>
    b.site_count !== a.site_count ? b.site_count - a.site_count : b.read_count - a.read_count
  );

  return {
    summary: {
      total_plates: results.length,
      total_reads: results.reduce((s, r) => s + r.read_count, 0),
      unique_sites: allSites.size,
      files_processed: filesProcessed,
    },
    plates: results,
  };
}

// ─── Hit-count badge/border colors ─────────────────────────────────────────────

function hitsStyle(siteCount) {
  if (siteCount >= 4) return { border: '#dc2626', badgeBg: '#fee2e2', badgeColor: '#991b1b' };
  if (siteCount === 3) return { border: '#f97316', badgeBg: '#ffedd5', badgeColor: '#9a3412' };
  if (siteCount === 2) return { border: '#f59e0b', badgeBg: '#fef3c7', badgeColor: '#92400e' };
  return { border: '#94a3b8', badgeBg: '#f1f5f9', badgeColor: '#64748b' };
}

// ─── Component ─────────────────────────────────────────────────────────────────

export default function WestchesterLPRTracker() {
  const [files, setFiles] = useState([]);
  const [minConf, setMinConf] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [results, setResults] = useState(null);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [openCards, setOpenCards] = useState(new Set());
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef();

  function addFiles(newFiles) {
    trackToolAction('/lprtracker', 'REKOR / ALPR Tracker');
    const csvFiles = Array.from(newFiles).filter(f => f.name.toLowerCase().endsWith('.csv'));
    setFiles(prev => {
      const combined = [...prev];
      for (const f of csvFiles) {
        if (!combined.find(u => u.name === f.name && u.size === f.size)) combined.push(f);
      }
      return combined;
    });
  }

  function removeFile(idx) {
    setFiles(prev => prev.filter((_, i) => i !== idx));
  }

  function toggleCard(idx) {
    setOpenCards(prev => {
      const next = new Set(prev);
      next.has(idx) ? next.delete(idx) : next.add(idx);
      return next;
    });
  }

  async function runAnalysis() {
    setLoading(true);
    setError('');
    setResults(null);
    setOpenCards(new Set());
    try {
      const data = await analyzeFiles(files, parseFloat(minConf) || 0);
      setResults(data);
      setFilter('all');
      setSearch('');
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  const visiblePlates = results
    ? results.plates
        .filter(p => filter !== 'multi' || p.site_count >= 2)
        .filter(p => !search || p.plate.includes(search.toUpperCase().trim()))
    : [];

  return (
    <>
      <Navbar pageTitle="REKOR / ALPR Tracker" />
      <Container maxWidth="md" sx={{ py: 4 }}>

        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a', letterSpacing: '-0.3px' }}>
            REKOR / <span style={{ color: '#dc2626' }}>ALPR Tracker</span>
          </Typography>
          <Typography sx={{ color: '#64748b', mt: '5px', fontSize: '15px' }}>
            Upload OpenALPR CSV exports from multiple cameras. Find plates that hit multiple sites, ranked from most to least locations.
          </Typography>
          <ReviewCaveat what="the original LPR records from the system that produced them" style={{ marginTop: 14 }} />
        </Box>

        {/* Upload card */}
        <Paper elevation={0} sx={{
          borderRadius: '14px',
          boxShadow: '0 1px 4px rgba(0,0,0,.08), 0 6px 24px rgba(0,0,0,.06)',
          p: '24px 28px', mb: 2.5,
        }}>

          {/* Drop zone */}
          <Box
            onClick={() => fileInputRef.current.click()}
            onDragOver={e => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={e => { e.preventDefault(); setDragOver(false); addFiles(e.dataTransfer.files); }}
            sx={{
              border: `2px dashed ${dragOver ? '#dc2626' : '#cbd5e1'}`,
              borderRadius: '10px', p: '36px 24px',
              textAlign: 'center', cursor: 'pointer',
              bgcolor: dragOver ? '#fff5f5' : 'transparent',
              color: dragOver ? '#dc2626' : '#64748b',
              transition: 'all .2s', userSelect: 'none',
              '&:hover': { borderColor: '#dc2626', bgcolor: '#fff5f5', color: '#dc2626' },
            }}
          >
            <svg width="44" height="44" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"
              style={{ display: 'block', margin: '0 auto 12px', opacity: .4 }}>
              <rect x="3" y="3" width="18" height="18" rx="2"/>
              <path d="M3 9h18M9 21V9"/>
            </svg>
            <Typography sx={{ fontSize: '15px', fontWeight: 600, mb: '3px' }}>
              Drop CSV files here &mdash; or click to browse
            </Typography>
            <Typography sx={{ fontSize: '13px', opacity: .75 }}>
              OpenALPR group results export &bull; Multiple files supported
            </Typography>
            <input
              ref={fileInputRef} type="file" accept=".csv" multiple
              style={{ display: 'none' }}
              onChange={e => { addFiles(e.target.files); e.target.value = ''; }}
            />
          </Box>

          {/* File list */}
          {files.length > 0 && (
            <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {files.map((f, i) => (
                <Box key={i} sx={{
                  display: 'flex', alignItems: 'center', gap: '10px',
                  p: '8px 12px', borderRadius: '7px',
                  bgcolor: '#fef2f2', border: '1px solid #fecaca', fontSize: '13px',
                }}>
                  <Box sx={{
                    flexShrink: 0, width: 30, height: 30,
                    bgcolor: '#fee2e2', color: '#dc2626', borderRadius: '5px',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '8px', fontWeight: 800, letterSpacing: '.5px',
                  }}>CSV</Box>
                  <Typography sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: 500, color: '#0f172a', fontSize: '13px' }}>
                    {f.name}
                  </Typography>
                  <Typography sx={{ color: '#94a3b8', whiteSpace: 'nowrap', fontSize: '13px' }}>
                    {fmtSize(f.size)}
                  </Typography>
                  <Box
                    component="button" onClick={() => removeFile(i)}
                    sx={{
                      background: 'none', border: 'none', cursor: 'pointer',
                      color: '#94a3b8', fontSize: '18px', lineHeight: 1, p: '0 2px',
                      borderRadius: '3px', transition: 'color .15s',
                      '&:hover': { color: '#dc2626' },
                    }}
                  >&times;</Box>
                </Box>
              ))}
            </Box>
          )}

          {/* Options row */}
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 2.5,
            mt: 2, pt: 2, borderTop: '1px solid #f1f5f9', flexWrap: 'wrap',
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: '13px', color: '#374151' }}>
              <Typography sx={{ fontSize: '13px', fontWeight: 600, whiteSpace: 'nowrap' }}>
                Min. confidence
              </Typography>
              <input
                type="number" value={minConf}
                onChange={e => setMinConf(e.target.value)}
                min={0} max={100} step={1}
                style={{
                  width: '68px', padding: '5px 8px',
                  border: '1.5px solid #e2e8f0', borderRadius: '6px',
                  fontSize: '13px', color: '#0f172a', background: '#f8fafc',
                  textAlign: 'center', outline: 'none',
                }}
              />
              <Typography sx={{ fontSize: '13px', color: '#94a3b8' }}>%</Typography>
            </Box>
            <Typography sx={{ fontSize: '12px', color: '#94a3b8', flex: 1 }}>
              Set above 0 to ignore low-confidence reads
            </Typography>
          </Box>

          {/* Analyze button */}
          <Button
            variant="contained" fullWidth
            onClick={runAnalysis}
            disabled={files.length === 0 || loading}
            sx={{
              mt: 2, p: '13px', bgcolor: '#dc2626',
              fontSize: '15px', fontWeight: 600, borderRadius: '8px',
              '&:hover': { bgcolor: '#b91c1c' },
              '&:disabled': { bgcolor: '#94a3b8' },
            }}
          >
            {loading
              ? 'Analyzing…'
              : files.length > 1 ? `Analyze ${files.length} Files` : 'Analyze'}
          </Button>

          {/* Error */}
          {error && (
            <Box sx={{
              display: 'flex', alignItems: 'center', gap: 1,
              p: '10px 14px', borderRadius: '8px', mt: 1.5,
              bgcolor: '#fef2f2', color: '#dc2626', fontSize: '14px',
            }}>
              {error}
            </Box>
          )}
        </Paper>

        {/* Results */}
        {results && (
          <>
            <GeneratedLegend style={{ marginBottom: 10 }}>
              Generated by LE Tech Tools: the grouping of reads by plate and the ranking by number of sites. Not part of the original LPR records.
            </GeneratedLegend>
            {/* Stats bar */}
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1.5, mb: 2 }}>
              {[
                { value: results.summary.total_plates, label: 'Unique Plates' },
                { value: results.summary.total_reads, label: 'Total Reads' },
                { value: results.summary.unique_sites, label: 'LPR Sites' },
                { value: results.summary.files_processed, label: 'Files Processed' },
              ].map(({ value, label }) => (
                <Paper key={label} elevation={0} sx={{
                  borderRadius: '10px', boxShadow: '0 1px 3px rgba(0,0,0,.07)',
                  p: '14px 16px', textAlign: 'center',
                }}>
                  <Typography sx={{ fontSize: '26px', fontWeight: 800, color: '#0f172a' }}>
                    {value.toLocaleString()}
                  </Typography>
                  <Typography sx={{ fontSize: '12px', color: '#64748b', mt: '2px', fontWeight: 500 }}>
                    {label}
                  </Typography>
                </Paper>
              ))}
            </Box>

            {/* Toolbar */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1.75, flexWrap: 'wrap' }}>
              <input
                type="text" placeholder="Filter by plate…"
                value={search}
                onChange={e => setSearch(e.target.value)}
                autoComplete="off" spellCheck="false"
                style={{
                  flex: 1, minWidth: '180px', padding: '9px 13px',
                  border: '1.5px solid #e2e8f0', borderRadius: '8px',
                  fontSize: '14px', color: '#0f172a', background: '#fff', outline: 'none',
                  fontFamily: "'SF Mono', 'Consolas', monospace", letterSpacing: '.5px',
                }}
              />
              <Box sx={{ display: 'flex', gap: '4px' }}>
                {[
                  { key: 'all', label: 'All Plates' },
                  { key: 'multi', label: '2+ Sites Only' },
                ].map(btn => (
                  <Box
                    key={btn.key}
                    component="button"
                    onClick={() => setFilter(btn.key)}
                    sx={{
                      px: '14px', py: '8px',
                      border: `1.5px solid ${filter === btn.key ? '#dc2626' : '#e2e8f0'}`,
                      borderRadius: '7px', fontSize: '13px', fontWeight: 600,
                      cursor: 'pointer', whiteSpace: 'nowrap',
                      bgcolor: filter === btn.key ? '#fef2f2' : '#fff',
                      color: filter === btn.key ? '#dc2626' : '#64748b',
                      transition: 'all .15s',
                    }}
                  >{btn.label}</Box>
                ))}
              </Box>
              <Typography sx={{ fontSize: '13px', color: '#94a3b8', ml: 'auto', whiteSpace: 'nowrap' }}>
                {visiblePlates.length.toLocaleString()} plates
              </Typography>
            </Box>

            {/* Plate list */}
            {visiblePlates.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 6, color: '#94a3b8' }}>
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"
                  style={{ display: 'block', margin: '0 auto 14px', opacity: .3 }}>
                  <circle cx="11" cy="11" r="8"/>
                  <path d="m21 21-4.35-4.35"/>
                </svg>
                <Typography sx={{ fontSize: '15px', fontWeight: 500, color: '#94a3b8' }}>
                  No plates match your filter.
                </Typography>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                {visiblePlates.map((p, idx) => {
                  const { border, badgeBg, badgeColor } = hitsStyle(p.site_count);
                  const isOpen = openCards.has(idx);
                  return (
                    <Paper key={idx} elevation={0} sx={{
                      borderRadius: '12px', overflow: 'hidden',
                      borderLeft: `4px solid ${border}`,
                      boxShadow: '0 1px 3px rgba(0,0,0,.06), 0 2px 10px rgba(0,0,0,.04)',
                    }}>
                      {/* Card header */}
                      <Box
                        onClick={() => toggleCard(idx)}
                        sx={{
                          display: 'flex', alignItems: 'center', gap: 1.5,
                          p: '14px 18px', cursor: 'pointer',
                          transition: 'background .15s',
                          '&:hover': { bgcolor: '#f8fafc' },
                        }}
                      >
                        {/* Site count badge */}
                        <Box sx={{
                          flexShrink: 0, minWidth: 36, height: 36, borderRadius: '8px',
                          display: 'flex', flexDirection: 'column',
                          alignItems: 'center', justifyContent: 'center',
                          bgcolor: badgeBg, color: badgeColor,
                        }}>
                          <span style={{ fontSize: '16px', fontWeight: 800, lineHeight: 1 }}>{p.site_count}</span>
                          <span style={{ fontSize: '8px', fontWeight: 700, letterSpacing: '.3px', marginTop: '1px' }}>
                            {p.site_count !== 1 ? 'SITES' : 'SITE'}
                          </span>
                        </Box>

                        {/* Plate number */}
                        <Typography sx={{
                          fontFamily: "'SF Mono', 'Consolas', 'Monaco', monospace",
                          fontSize: '18px', fontWeight: 800, letterSpacing: '1.5px', color: '#0f172a',
                        }}>{p.plate}</Typography>

                        {/* State badge */}
                        {p.state && (
                          <Box sx={{
                            bgcolor: '#1e293b', color: '#fff',
                            fontSize: '10px', fontWeight: 800, letterSpacing: '.5px',
                            px: '7px', py: '2px', borderRadius: '4px', flexShrink: 0,
                          }}>{p.state}</Box>
                        )}

                        {/* Meta */}
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography sx={{ fontSize: '12px', color: '#94a3b8' }}>
                            {p.read_count} total read{p.read_count !== 1 ? 's' : ''}
                          </Typography>
                          {p.vehicle && (
                            <Typography sx={{ fontSize: '13px', color: '#475569', mt: '1px' }}>
                              {p.vehicle}
                            </Typography>
                          )}
                        </Box>

                        {/* Expand chevron */}
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                          stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                          style={{ flexShrink: 0, transition: 'transform .2s', transform: isOpen ? 'rotate(180deg)' : 'none' }}>
                          <path d="M6 9l6 6 6-6"/>
                        </svg>
                      </Box>

                      {/* Site breakdown */}
                      {isOpen && (
                        <Box sx={{
                          px: '18px', pb: '14px', pt: '12px',
                          borderTop: '1px solid #f1f5f9',
                          display: 'flex', flexDirection: 'column', gap: 1,
                        }}>
                          {p.sites.map((s, si) => {
                            const first = fmtTimestamp(s.first_seen);
                            const last = fmtTimestamp(s.last_seen);
                            const timeStr = first === last
                              ? (first ? `Seen: ${first}` : '')
                              : (first ? `First: ${first}  •  Last: ${last}` : '');
                            return (
                              <Box key={si} sx={{
                                display: 'flex', alignItems: 'flex-start', gap: 1.25,
                                p: '10px 12px', borderRadius: '8px', bgcolor: '#f8fafc',
                              }}>
                                <Box sx={{
                                  flexShrink: 0, width: 8, height: 8, borderRadius: '50%',
                                  bgcolor: '#dc2626', mt: '4px',
                                }}/>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                  <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#0f172a', mb: '2px' }}>
                                    {s.name}
                                  </Typography>
                                  {s.camera && s.camera !== s.name && (
                                    <Typography sx={{ fontSize: '12px', color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                      {s.camera}
                                    </Typography>
                                  )}
                                  {timeStr && (
                                    <Typography sx={{ fontSize: '11px', color: '#94a3b8', mt: '3px' }}>
                                      {timeStr}
                                    </Typography>
                                  )}
                                </Box>
                                <Box sx={{
                                  flexShrink: 0, px: '10px', py: '2px', borderRadius: '99px',
                                  bgcolor: '#fef2f2', color: '#dc2626',
                                  fontSize: '12px', fontWeight: 700, whiteSpace: 'nowrap',
                                }}>
                                  {s.reads} read{s.reads !== 1 ? 's' : ''}
                                </Box>
                              </Box>
                            );
                          })}
                        </Box>
                      )}
                    </Paper>
                  );
                })}
              </Box>
            )}
          </>
        )}
      </Container>
    </>
  );
}
